import { useGetSaltoSpaceAccessPoints } from '@/api/keyless/salto-space/get-salto-space-access-points'
import { useGetSaltoSpaceConnection } from '@/api/property-keyless/salto-space/get-salto-space-connection'

import { SaltoSpaceConnectionFormValues, saltoSpaceDefaultValues } from './salto-space.schema'

export const useSaltoSpaceConnection = (buildingId: number) => {
  const { data, isLoading, isSuccess } = useGetSaltoSpaceConnection({ buildingId })
  // Note: reused as-is — same building-scoped `/salto-space/access-points/{buildingId}`
  // endpoint the legacy card calls to populate the zone/access-level dropdown options.
  const {
    data: accessPoints,
    isLoading: isLoadingAccessPoints,
    isSuccess: isAccessPointsSuccess,
    refetch: refetchAccessPoints,
  } = useGetSaltoSpaceAccessPoints({
    buildingId,
  })

  // Note: unlike other providers, "configured" here also requires the access-points call to
  // succeed — it's a live call to Salto Space's own API using the saved apiKey, so its failure
  // means the vendor itself rejects that apiKey (not merely "our DB has a value saved").
  const isConfigured = isSuccess && isAccessPointsSuccess && Boolean(data?.apiKey)

  const values: SaltoSpaceConnectionFormValues = {
    ...saltoSpaceDefaultValues,
    apiKey: data?.apiKey ?? '',
    residentAccessLevelIds: (data?.config?.accessLevels ?? []).map((a) => a.ExtGroupID),
    residentZoneIds: (data?.config?.permissionGroups ?? []).map((g) => g.ExtZoneID),
    shouldAutoAssignApartmentDoor: data?.config?.shouldAutoAssignApartmentDoor ?? false,
    shouldAutoAssignUserAccessLevel: data?.config?.shouldAutoAssignUserAccessLevel ?? false,
    url: data?.url ?? '',
    visitorAccessLevelIds: (data?.config?.visitorAccessLevels ?? []).map((a) => a.ExtGroupID),
    visitorZoneIds: (data?.config?.visitorPermissionGroups ?? []).map((g) => g.ExtZoneID),
  }

  return {
    // Note: {saltoId, name} shape — matches legacy's react-select getOptionValue/getOptionLabel
    // usage directly (see salto-space-fields.tsx), not a {label, value} pair.
    accessLevels: accessPoints?.accessLevels ?? [],
    isConfigured,
    isLoading: isLoading || isLoadingAccessPoints,
    isLoadingAccessPoints,
    refetchAccessPoints,
    values,
    zones: accessPoints?.permissionGroups ?? [],
  }
}
